
import { Mixins, Component, Ref, Prop } from "vue-property-decorator";
import { onView } from "_mixins";
import { config } from "_core";
import { SmartImage, SmartImageProps } from "_components";
import {
  VertexShader,
  FragmentShader,
  DEFAULT_DELTAS,
  Curtains,
  Vec2,
  Plane,
} from "./";
import { EventTakeoverVisual } from "@app/views/Home/types";

@Component({
  name: "EventTakeoverWebGL",
  components: { SmartImage },
})
export default class EventTakeoverWebGL extends Mixins(onView) {
  rootClass = "p-event-shader";
  @Prop() data: EventTakeoverVisual;
  @Prop() isMob: boolean;
  @Ref() canvas: HTMLElement;
  @Ref() plane: HTMLElement;

  mousePosition = new Vec2();
  mouseLastPosition = new Vec2();
  deltas = DEFAULT_DELTAS;

  webGLCurtain: Curtains | null = null;
  webGLplane: Plane | null = null;

  listenersEnabled = false;
  loaded = false;

  handleLoad() {
    if (!this.loaded) {
      this.loaded = true;
      if (this.shouldRunWebGL) setTimeout(() => this.init(), 1000);
      else this.$emit("loaded");
    }
  }

  get displacementImg(): SmartImageProps {
    if (!this.data) return null;
    const image =
      this.isMob && this.data.sheet_effect.displacement_mobile
        ? this.data.sheet_effect.displacement_mobile
        : this.data.sheet_effect.displacement;
    return (
      image && {
        image,
        lazyload: false,
        fit: "cover",
      }
    );
  }

  get shouldRunWebGL() {
    return config.isHighPerf;
  }

  public init() {
    const params = {
      vertexShader: VertexShader,
      fragmentShader: FragmentShader,
      widthSegments: 20,
      heightSegments: 10,
      uniforms: {
        mouseTime: {
          name: "uMouseTime",
          type: "1f",
          value: 0,
        },
        mousePosition: {
          name: "uMousePosition",
          type: "2f",
          value: this.mousePosition,
        },
        mouseMoveStrength: {
          name: "uMouseMoveStrength",
          type: "1f",
          value: 0,
        },
      },
    };

    if (!this.webGLCurtain && this.canvas) {
      this.webGLCurtain = new Curtains({
        container: this.canvas,
        premultipliedAlpha: true,
        watchScroll: false,
        production: true,
      });
    }

    if (!this.webGLplane && this.webGLCurtain && this.plane) {
      this.webGLplane = new Plane(this.webGLCurtain, this.plane, params);
    }

    this.webGLplane &&
      this.webGLplane
        .onReady(() => {
          this.webGLplane.setPerspective(35);
          this.setupListeners(true);
        })
        .onRender(() => {
          this.webGLplane.uniforms.mouseTime.value++;

          this.deltas.applied +=
            (this.deltas.max - this.deltas.applied) * 0.01 + 0.01;
          this.deltas.max += (0 - this.deltas.max) * 0.01;

          this.webGLplane.uniforms.mouseMoveStrength.value =
            this.deltas.applied;

          this.$emit("loaded");
        });
  }

  private setupListeners(bool: boolean = false) {
    const listenChannels = ["mousemove", "touchmove"];
    const host = document.body;

    listenChannels.forEach((channel) => {
      if (bool) host.addEventListener(channel, this.handleMovement);
      else host.removeEventListener(channel, this.handleMovement);
    });

    this.listenersEnabled = bool;
  }

  private handleMovement(e) {
    if (!this.isInView || !this.webGLCurtain) return;
    this.mouseLastPosition.copy(this.mousePosition);
    const mouse = new Vec2();

    if (e.targetTouches)
      mouse.set(e.targetTouches[0].clientX, e.targetTouches[0].clientY);
    else mouse.set(e.clientX, e.clientY);

    this.mousePosition.set(
      this.webGLCurtain.lerp(this.mousePosition.x, mouse.x, 0.3),
      this.webGLCurtain.lerp(this.mousePosition.y, mouse.y, 0.3)
    );

    if (this.webGLplane) {
      const mouseCoords = this.webGLplane.mouseToPlaneCoords(
        this.mousePosition
      );

      if (mouseCoords.y > -1.25) {
        this.webGLplane.uniforms.mousePosition.value = mouseCoords;

        if (this.mouseLastPosition.x && this.mouseLastPosition.y) {
          let delta =
            Math.sqrt(
              Math.pow(this.mousePosition.x - this.mouseLastPosition.x, 2) +
                Math.pow(this.mousePosition.y - this.mouseLastPosition.y, 2)
            ) / 30;
          delta = Math.min(4, delta);
          if (delta >= this.deltas.max) {
            this.deltas.max = delta;
          }
        }
      }
    }
  }

  onView(inView: boolean) {
    if (inView !== this.listenersEnabled) this.setupListeners(inView);
  }

  beforeDestroy() {
    this.setupListeners(false);
    this.webGLCurtain.dispose();
    // console.log(this.webGLCurtain);
  }
}
